import { Table } from 'antd';
import moment from 'moment';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';

import ModalWrap from '@/components/ModalWrap';
import TableAdmin from '@/components/TableAdmin';
import { JPY } from '@/constants/character';
import revenueService from '@/services/revenue.service';
import { handleMessageError } from '@/util/handleMessageError';
import { exportCSVFile } from '@/util/jsonToCsv';
import { numberFormatWithComma } from '@/util/numberFormatWithComma';
import './style.scss';

interface IProps {
    isVisible: boolean;
    data: any;
    onCancel: (isVisible: boolean) => void;
}
const Detail: React.FC<IProps> = ({ isVisible, onCancel, data }) => {
    const { t } = useTranslation();
    const sales = data?.sales;

    const { mutate } = useMutation((id: any) => revenueService.getRevenueDetailDownload(id), {
        onSuccess: (data: any) => {
            exportCSVFile({ data, fileName: t('revenue:REVENUE_DETAILS_2') });
        },
        onError: (error: any) => {
            handleMessageError(error, t);
        },
    });

    const exportCsv = () => {
        mutate(sales?.id);
    };

    const columns = [
        {
            title: t('PRODUCT_NAME'),
            dataIndex: 'itemName',
        },
        {
            title: t('PRODUCT_NUMBER'),
            dataIndex: 'hinban',
        },
        {
            title: t('JAN_CODE'),
            dataIndex: 'janCode',
        },
        {
            title: t('UNIT_PRICE'),
            dataIndex: 'unitPrice',
            render: (value: number) => <span> {numberFormatWithComma(value, JPY)} </span>,
        },
        {
            title: t('QUANTITY'),
            dataIndex: 'num',
            render: (value: number) => numberFormatWithComma(value),
        },
        {
            title: t('SUB_TOTAL'),
            dataIndex: 'subTotal',
            render: (value: number) => numberFormatWithComma(value, JPY),
        },
        {
            title: t('DETAILED_DISCOUNT_AMOUNT'),
            dataIndex: 'discountPrice',
            render: (value: number) => numberFormatWithComma(value, JPY),
        },
        {
            title: t('TOTAL'),
            dataIndex: 'total',
            render: (value: number) => numberFormatWithComma(value, JPY),
        },
    ];

    const dataTable = useMemo(
        () =>
            data?.salesDetail?.data?.map((item: any, index: number) => ({
                ...item,
                rowKey: `${data?.sales?.id}_${index}`,
            })),
        [data],
    );

    return (
        <ModalWrap
            visible={isVisible}
            isFooterCancel
            onCancel={() => onCancel(!isVisible)}
            title={t('revenue:REVENUE_DETAILS_2')}
            isExportCsv
            onExportCsv={exportCsv}
        >
            <div className="p-revenue-content__detail">
                <div>{t('daily-report:TH5')}</div>
                <table className="table-text">
                    <tbody>
                        <tr>
                            <th>{t('revenue:th1')}</th>
                            <td>{sales?.salesNo}</td>
                            <th>{t('revenue:th2')}</th>
                            <td>{numberFormatWithComma(sales?.cash, JPY)}</td>
                        </tr>
                        <tr>
                            <th>{t('revenue:th3')}</th>
                            <td>{sales?.salesDiv === 2 ? '赤伝' : '通常'}</td>
                            <th>{t('revenue:th4')}</th>
                            <td>{numberFormatWithComma(sales?.cash + sales?.change, JPY)}</td>
                        </tr>
                        <tr>
                            <th>{t('revenue:th5')}</th>
                            <td>{sales?.salesDate && moment(sales?.salesDate).format('YYYY/MM/DD')}</td>
                            <th>{t('revenue:th6')}</th>
                            <td>{numberFormatWithComma(sales?.change, JPY)}</td>
                        </tr>
                        <tr>
                            <th>{t('revenue:th7')}</th>
                            <td>{sales?.salesTime}</td>
                            <th>{t('revenue:th8')}</th>
                            <td>{numberFormatWithComma(sales?.credit, JPY)}</td>
                        </tr>
                        <tr>
                            <th>{t('revenue:th9')}</th>
                            <td>{numberFormatWithComma(sales?.detailTotal, JPY)}</td>
                            <th>{t('revenue:th25')}</th>
                            <td></td>
                        </tr>
                        <tr>
                            <th>{t('revenue:th11')}</th>
                            <td>{numberFormatWithComma(sales?.discountDetailTotal, JPY)}</td>
                            <th>{t('revenue:th12')}</th>
                            <td></td>
                        </tr>
                        <tr>
                            <th>{t('revenue:th13')}</th>
                            <td>{numberFormatWithComma(sales?.subTotal, JPY)}</td>
                            <th>&nbsp;</th>
                            <td>&nbsp;</td>
                        </tr>
                        <tr>
                            <th>{t('revenue:th15')}</th>
                            <td>{numberFormatWithComma(sales?.discountPrice, JPY)}</td>
                            <th>&nbsp;</th>
                            <td>&nbsp;</td>
                        </tr>
                        <tr>
                            <th>{t('revenue:th29')}</th>
                            <td>{numberFormatWithComma(sales?.discountCouplePrice, JPY)}</td>
                            <th>&nbsp;</th>
                            <td>&nbsp;</td>
                        </tr>
                        <tr>
                            <th>{t('revenue:th30')}</th>
                            <td>{numberFormatWithComma(sales?.discountMobileAppPrice, JPY)}</td>
                            <th>&nbsp;</th>
                            <td>&nbsp;</td>
                        </tr>
                        <tr>
                            <th>{t('revenue:th28')}</th>
                            <td>{numberFormatWithComma(sales?.feeDifferent, JPY)}</td>
                            <th>&nbsp;</th>
                            <td>&nbsp;</td>
                        </tr>
                        <tr>
                            <th>{t('revenue:th19')}</th>
                            <td>{numberFormatWithComma(sales?.total, JPY)}</td>
                            <td>&nbsp;</td>
                            <td>&nbsp;</td>
                        </tr>
                        <tr>
                            <th>{t('revenue:th20')}</th>
                            <td>{numberFormatWithComma(sales?.tax, JPY)}</td>
                            <td>&nbsp;</td>
                            <td>&nbsp;</td>
                        </tr>
                        <tr>
                            <th>&nbsp;</th>
                            <td>&nbsp;</td>
                            <td>&nbsp;</td>
                            <td>&nbsp;</td>
                        </tr>
                    </tbody>
                </table>
                <div>
                    <div className="flex">
                        <span className="title">{t('revenue:th22')}</span>
                        <span className="content">{sales?.baseSalesNo}</span>
                    </div>
                    <div className="flex">
                        <span className="title">{t('revenue:th23')}</span>
                        <span className="content">{sales?.description}</span>
                    </div>
                    <div className="flex">
                        <span className="title">{t('revenue:th24')}</span>
                        <span className="content">{sales?.editStaffName}</span>
                    </div>
                </div>
                <span>{t('revenue:th26')}</span>
                <TableAdmin
                    columns={columns}
                    data={dataTable}
                    bordered
                    rowKey="rowKey"
                    pointer
                    scroll={{ x: 'max-content' }}
                    summary={() => {
                        return (
                            <>
                                <Table.Summary.Row>
                                    <Table.Summary.Cell align="center" index={0} colSpan={4} className="total-cell">
                                        {t('TOTAL')}
                                    </Table.Summary.Cell>

                                    <Table.Summary.Cell index={1}>
                                        {numberFormatWithComma(data?.salesDetail?.total?.sum_num)}
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={2}>
                                        {numberFormatWithComma(data?.salesDetail?.total?.sum_subTotal, JPY)}
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={3}>
                                        {numberFormatWithComma(data?.salesDetail?.total?.sum_discountPrice, JPY)}
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={4}>
                                        {numberFormatWithComma(data?.salesDetail?.total?.sum_total, JPY)}
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            </>
                        );
                    }}
                />
            </div>
        </ModalWrap>
    );
};

export default memo(Detail);
